<template>
  <div class="flex h-screen w-screen flex-col overflow-hidden bg-white">
    <FormNav
      :title="metaData.title"
      :total-count="metaData.totalQuestionCount"
      :activeField="formState?.activeFieldIndex || 0"
      :isLastField="formState?.isLastField || false"
      :isFirstField="formState?.isFirstField || false"
      :is-external="metaData.isExternal"
      :isSidebarOpen="isSidebarOpen"
      @previous-question="back"
      @next="onSubmit"
    />
    <div class="flex flex-1 overflow-hidden pt-[120px]">
      <QuestionnaireSidebar
        :rawSchema="rawSchema"
        :formFields="formFields"
        :activeField="formState.activeFieldId"
        :orgName="clientActionsStore.clientAction?.org_name || 'Jupus'"
        :orgLogo="clientActionsStore.clientOrganisation?.logo"
        class="z-40"
        @jump-to-field="jumpToField"
        @sidebar-toggle="handleSidebarToggle"
      />
      <div
        class="flex flex-1 flex-col overflow-y-auto transition-all duration-300 ease-in-out"
        :class="[isSidebarOpen ? 'ml-64 sm:ml-72' : 'ml-0']"
      >
        <div class="flex flex-1 flex-col items-center justify-center p-4">
          <template v-if="!formState.isLastField">
            <FieldGroup
              :fieldId="formState.activeFieldId"
              :activeField="formState.activeFieldId"
              :next="formState.next"
              class="mb-8 flex w-full max-w-2xl flex-col gap-4"
            >
              <button
                class="flex items-center gap-2 font-[Inter] text-xs font-semibold tracking-tight text-gray-400"
                @click="back"
              >
                <QuestionairePrevIcon />
                <div class="text-center">Vorherige Frage</div>
              </button>
              <div class="flex flex-col space-y-4">
                <div
                  class="text-wrap font-[Inter] text-[28px] font-semibold leading-normal tracking-tight text-stone-600"
                >
                  <span>{{ activeField.label }}</span>
                </div>
                <FieldHelp
                  v-if="activeField.help"
                  :help-text="activeField.help"
                  class="mt-1"
                />
                <div class="w-full">
                  <div class="flex w-full flex-col items-start justify-start gap-2.5">
                    <Component
                      :is="activeField.component"
                      v-model="store.formData[activeField.uuid]"
                      :field="getComponentFieldMeta(activeField)"
                      :is-preview-mode="metaData.isPreview"
                      @files-changed="
                        processChange(activeField.uuid, store.formData[activeField.uuid])
                      "
                    />
                    <FieldError
                      :button-text="activeField.buttonText"
                      :silentErrors="v.$silentErrors"
                      :showValidateErrors="!metaData.isPreview"
                      @back="back"
                      @next="handleNext"
                      @jump-to-results-page="setToLastField"
                    />
                  </div>
                </div>
              </div>
            </FieldGroup>
          </template>
          <FormResult
            v-if="formState.isLastField"
            :isLastField="formState.isLastField"
            :form-fields="formFields"
            :is-external="metaData.isExternal"
            :is-preview-mode="metaData.isPreview"
            @cell-click="jumpToField"
          />
        </div>
        <div class="flex w-full items-center justify-center bg-white py-2">
          <div class="font-[Inter] text-[15px] font-medium tracking-tight text-black">
            Powered by
          </div>
          <img
            class="ml-[9px] h-[30px] w-[72px]"
            src="https://s3-eu-central-1.ionoscloud.com/jupus/images/logo.svg"
            alt="Jupus Logo"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue'
  import { useVuelidate } from '@vuelidate/core'
  import { useQuestionnaireStore } from '@/store/questionaireStore'
  import { useClientActionStore } from '@/store/clientActionStore'
  import { useToast } from '@/components/ui/toast/use-toast'
  import { pick } from '@/lib/utils'
  import QuestionnaireSidebar from './questionnaireSidebar.vue'
  import FieldGroup from './formElements/fieldGroup.vue'
  import FieldError from './formElements/fieldError.vue'
  import FormNav from './formElements/formNav.vue'
  import FormResult from './formElements/formResult.vue'
  import FieldHelp from '@/components/questionaire/formElements/fieldHelp.vue'
  import QuestionairePrevIcon from '@/components/icons/questionairePrevIcon.vue'

  const { toast } = useToast()

  const props = defineProps({
    formFields: {
      type: Array,
      default: () => []
    },
    metaData: {
      type: Object,
      default: () => ({})
    },
    rawSchema: {
      type: Array,
      default: () => []
    },
    formState: {
      type: Object,
      default: () => ({})
    },
    validateField: Function,
    onSubmit: Function,
    back: Function,
    next: Function,
    jumpToField: Function,
    setToLastField: Function,
    getFieldByUuid: Function,
    handleFieldChange: Function
  })

  const v = useVuelidate({ $stopPropagation: true })
  const store = useQuestionnaireStore()
  const clientActionsStore = useClientActionStore()

  const isSidebarOpen = ref(true)

  const activeField = computed(() => {
    return props.getFieldByUuid(props.formState.activeFieldId) || {}
  })

  function handleSidebarToggle(isOpen: boolean) {
    isSidebarOpen.value = isOpen
  }

  const processChange = (uuid: string, value: any) => {
    props.handleFieldChange(uuid, value)

    if (props.metaData.isPreview) {
      return
    }
    clientActionsStore
      .updateSubmissionItem(uuid, value)
      .then((status) => {
        if (status !== 200) {
          toast({
            title: 'Oh, oh!',
            description: 'Something went wrong. Please try again.',
            variant: 'destructive'
          })
        }
      })
      .catch((_error) => {
        toast({
          title: 'Oh, oh!',
          description: 'Something went wrong. Please try again.',
          variant: 'destructive'
        })
      })
  }

  function getComponentFieldMeta(field: any) {
    const fieldMeta = pick(field, [
      'uuid',
      'validation',
      'type',
      'text',
      'choices',
      'label',
      'optional',
      'value'
    ])

    return fieldMeta
  }

  function onFieldChange(uuid: any, value: any) {
    props.handleFieldChange(uuid, value)
  }

  function handleNext() {
    // Process changes for the current field
    processChange(props.formState.activeFieldId, store.formData[props.formState.activeFieldId])

    // Call the original onSubmit function
    props.onSubmit()
  }
</script>
